import { DisplayCity, FurnishedMarketAPI_Data } from '@sales-app/api/DisplayCity/DisplayCity'
import { transformToFavoritePinnedAPIQuery } from '@sales-app/context/workspace/homeFilters.util'
import { useWorkspaceContext } from '@sales-app/context/workspace/workspace.hooks'
import { FavoriteQueries } from '@sales-app/graphql/queries/potentialBooking/favorite.queries'
import { PinnedHomesQueries } from '@sales-app/graphql/queries/potentialBooking/pinnedHomes.queries'
import { QueryObserverResult } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'

import { queryClient } from '@/api/queryClient'
import { createAPIQueryFunction, createAPIQueryKey } from '@/hooks/useAPI/useAPI.util'
import { useGraphql } from '@/hooks/useGraphql'

import { DisplayCityData } from '../useRecommendedHomes/useRecommendedHomes.util'
import { getFavoritesPinnedData, getHomesFromFavoritesPinned, RecommendedHomeRow } from './useFavoritesPinnedHomes.util'

export function useFavoritesPinnedHomes() {
  const [{ tabs, activeTabId, activeTabType }] = useWorkspaceContext()
  const [favPinnedHomes, setFavPinnedHomes] = useState<RecommendedHomeRow[]>()
  const [isFavPinnedHomesLoading, setIsFavPinnedHomesLoading] = useState(false)
  const bookablePerson = tabs.find((t) => t.bookablePersonId === activeTabId && t.bookablePersonType === activeTabType)

  const favoritePinnedAPIQueryParams = useMemo(() => {
    const filters = bookablePerson?.potentialBookingFilters || { listView: true }
    return transformToFavoritePinnedAPIQuery(filters)
  }, [bookablePerson?.potentialBookingFilters])

  const {
    data: pinnedData,
    refetch: pinRefetch,
    isLoading: isPinnedDataLoading,
  } = useGraphql<PinnedHomesQueries.GET.Response>(
    PinnedHomesQueries.getPinnedHomes.name,
    PinnedHomesQueries.getPinnedHomes(),
    { filter: { potentialBookingId: bookablePerson?.potentialBookingId } },
    { enabled: bookablePerson && !!bookablePerson.potentialBookingId },
  )

  const {
    data: favoritesData,
    refetch: favRefetch,
    isLoading: isFavoriteLoading,
  } = useGraphql<FavoriteQueries.GET.Response>(
    FavoriteQueries.getFavorites.name,
    FavoriteQueries.getFavorites(),
    {
      bookablePersonRecord: {
        bookablePersonId: bookablePerson?.bookablePersonId,
        bookablePersonType: bookablePerson?.bookablePersonType,
      },
    },
    {
      enabled: bookablePerson && bookablePerson.userId !== undefined,
    },
  )

  const isLoading = isFavoriteLoading || isPinnedDataLoading

  const pinnedHomesData = useMemo(() => {
    return pinnedData?.potentialBookingPinnedHomes ? pinnedData?.potentialBookingPinnedHomes : []
  }, [pinnedData?.potentialBookingPinnedHomes])
  const favoriteHomesData = useMemo(() => {
    return favoritesData?.bookablePerson?.favorites ? favoritesData?.bookablePerson?.favorites : []
  }, [favoritesData?.bookablePerson?.favorites])

  const { homes, cityHomeMap } = useMemo(() => {
    return getHomesFromFavoritesPinned(favoriteHomesData, pinnedHomesData, bookablePerson?.potentialBookingId)
  }, [pinnedHomesData, favoriteHomesData, bookablePerson?.potentialBookingId])

  useEffect(() => {
    const displayCities = Object.keys(cityHomeMap)
    if (displayCities.length) {
      setIsFavPinnedHomesLoading(true)
      loadFavPinnedHomes()
    }

    async function loadFavPinnedHomes() {
      const responses = await Promise.all(
        displayCities.map(async (slug) => {
          const homeIds = [...new Set(cityHomeMap[slug] || [])]
          const homeIdsParam = homeIds.reduce((p, c, i) => (i > 0 ? p + ',' + c : p + '' + c), '')
          const queryFnOptions = {
            params: { home_ids: homeIdsParam, ...favoritePinnedAPIQueryParams },
          }
          const queryFn = createAPIQueryFunction(DisplayCity.getHomes(slug), queryFnOptions)
          const queryKey = createAPIQueryKey(DisplayCity.getHomes(slug), queryFnOptions)

          const res = await queryClient.fetchQuery({ queryKey, queryFn })
          if (res && res.data) {
            return (await res.data) as FurnishedMarketAPI_Data
          }
        }),
      )
      const pinHomeData: RecommendedHomeRow[] = []
      responses.forEach((res) => {
        if (res?.homes) {
          pinHomeData.push(...getFavoritesPinnedData(homes, res, bookablePerson))
        }
      })

      setFavPinnedHomes(pinHomeData)
      setIsFavPinnedHomesLoading(false)
    }
  }, [bookablePerson, cityHomeMap, favoritePinnedAPIQueryParams, homes])

  const data = useMemo(() => {
    return favPinnedHomes
  }, [favPinnedHomes])

  const handleRefetch = () => {
    pinRefetch()
    favRefetch()
  }

  return {
    data,
    isLoading: isLoading || isFavPinnedHomesLoading,
    refetch: handleRefetch,
    homes: homes,
  }
}

export type RecommendedHomesQuery<T> = Omit<QueryObserverResult<T>, 'data'> & {
  currentPage: number
  data?: { data: T }
  hasNextPage: boolean
  homePerPage: number
  isWithinZoomBounds: boolean
  totalFiltersApplied?: number
  totalPages: number
}

export type UseRecommendedHomes = RecommendedHomesQuery<DisplayCityData>
